import React from 'react';
import Modal from 'react-modal';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay } from 'date-fns';
import { fr } from 'date-fns/locale';

const customStyles = {
  content: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    marginRight: '-50%',
    transform: 'translate(-50%, -50%)',
    maxWidth: '90vw',
    width: '640px',
    maxHeight: '90vh',
    padding: 0,
    border: 'none',
    borderRadius: '12px',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    overflow: 'hidden',
    zIndex: 10000,
  },
  overlay: {
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    zIndex: 9999,
  },
};

const AgentDetailsModal = ({ isOpen, closeModal, agent, selectedMonth }) => {
  if (!agent) return null;

  const renderAgentAvailability = () => {
    if (!selectedMonth) return null;

    const monthStart = startOfMonth(new Date(selectedMonth));
    const monthEnd = endOfMonth(new Date(selectedMonth));
    const days = eachDayOfInterval({ start: monthStart, end: monthEnd });
    const availabilities = agent.availabilities ?? [];
    const availabilityForSelectedMonth =
      availabilities.find((avail) => avail.month === selectedMonth)?.availability ?? [];

    return (
      <div className="p-5">
        <div className="flex items-center gap-3 mb-4">
          <span className="inline-block w-3 h-3 rounded-full bg-[#FAB615]" />
          <span className="text-sm font-medium text-gray-600">Jour</span>
          <span className="inline-block w-3 h-3 rounded-full bg-[#B0B0B0]" />
          <span className="text-sm font-medium text-gray-600">Nuit</span>
        </div>
        <div className="grid grid-cols-7 gap-1.5">
          {['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'].map((d) => (
            <div key={d} className="text-center text-xs font-semibold text-gray-500 py-1">
              {d}
            </div>
          ))}
          {days.map((day, index) => {
            const availability = availabilityForSelectedMonth.find((avail) =>
              isSameDay(new Date(avail.date), day)
            );
            return (
              <div
                key={index}
                className="min-h-[52px] rounded-lg border border-gray-100 bg-gray-50/50 p-2 flex flex-col items-center justify-center"
              >
                <span className="text-xs font-medium text-gray-600">{format(day, 'dd')}</span>
                {availability ? (
                  <div className="mt-1 flex flex-wrap justify-center gap-0.5">
                    {availability.day && (
                      <span className="inline-block px-1.5 py-0.5 rounded text-[11px] font-medium bg-[#FAB615] text-slate-800">
                        J
                      </span>
                    )}
                    {availability.night && (
                      <span className="inline-block px-1.5 py-0.5 rounded text-[11px] font-medium bg-[#B0B0B0] text-slate-800">
                        N
                      </span>
                    )}
                  </div>
                ) : (
                  <span className="mt-1 text-[10px] text-gray-400">—</span>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={closeModal}
      contentLabel="Détails de l'Agent"
      style={customStyles}
      ariaHideApp={false}
    >
      <div className="flex flex-col max-h-[90vh] bg-white overflow-x-hidden">
        <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-b border-gray-200 bg-gray-50 shrink-0">
          <h2 className="text-lg sm:text-xl font-bold text-gray-800">Détails de l'Agent</h2>
          <button
            type="button"
            onClick={closeModal}
            className="p-2.5 min-h-[44px] min-w-[44px] flex items-center justify-center rounded-lg text-gray-500 hover:bg-gray-200 hover:text-gray-700 focus:outline-none transition-colors"
            aria-label="Fermer"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="overflow-y-auto overflow-x-hidden flex-1 min-h-0">
          <div className="px-6 py-4 space-y-1 border-b border-gray-100">
            <p className="text-base">
              <span className="text-gray-500 font-medium">Nom complet :</span>{' '}
              <span className="text-gray-900 font-semibold">
                {agent.firstName} {agent.lastName}
              </span>
            </p>
            <p className="text-base">
              <span className="text-gray-500 font-medium">Email :</span>{' '}
              <span className="text-gray-900">{agent.email ?? '—'}</span>
            </p>
            <p className="text-base">
              <span className="text-gray-500 font-medium">Poste :</span>{' '}
              <span className="text-gray-900">{agent.poste ?? '—'}</span>
            </p>
            <p className="text-base">
              <span className="text-gray-500 font-medium">Matricules :</span>{' '}
              <span className="text-gray-900">{agent.matricules ?? 'N/A'}</span>
            </p>
          </div>

          {selectedMonth && (
            <div className="border-t border-gray-100">
              <h3 className="px-6 py-3 text-sm font-semibold text-gray-700 bg-gray-50 border-b border-gray-100">
                Disponibilités — {format(new Date(selectedMonth + '-01'), 'MMMM yyyy', { locale: fr })}
              </h3>
              {renderAgentAvailability()}
            </div>
          )}
        </div>

        <div className="px-4 sm:px-6 py-4 border-t border-gray-200 bg-gray-50 shrink-0">
          <button
            type="button"
            onClick={closeModal}
            className="w-full py-3 sm:py-2.5 px-4 min-h-[44px] bg-gray-800 text-white text-sm font-medium rounded-lg hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors"
          >
            Fermer
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default AgentDetailsModal;
